import { Head, router } from "@inertiajs/react"
import { useMemo, useState } from "react"

import {
  HpiDataTable,
  HpiDateFilterBar,
  HpiFilterSelect,
  HpiPagination,
  HpiStatsBar,
  WorkflowStatusBadge,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import type { WorkflowStatus } from "@/components/hpi"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface Activity {
  id: string
  source_id: number
  activity_type: "Time" | "Expense"
  sub_type: string | null
  activity_date: string | null
  case_name: string | null
  case_id: number | null
  marketing_activity_id: number | null
  description: string | null
  added_at: string | null
  submitted_at: string | null
  approved_at: string | null
  paid_at: string | null
  paid_na: boolean
  paid_by: string | null
  receipt_type: string | null
  employee_name?: string
  expense_amount: number
  otj_minutes: number
  travel_minutes: number
  mileage: number
  was_rejected?: boolean
  is_mine?: boolean
}

interface GroupedActivities {
  unsubmitted: Activity[]
  rejected: Activity[]
  submitted: Activity[]
  approved: Activity[]
  paid: Activity[]
}

interface EmployeeOption {
  id: number
  name: string
}

interface Summary {
  type: "all" | "time" | "mileage" | "expenses"
  otj?: string
  travel?: string
  mileage?: number
  total?: number
}

interface Props {
  employee: { id: number; name: string }
  activities: Activity[]
  grouped_activities: GroupedActivities
  start_date: string
  end_date: string
  type_filter: string
  summary: Summary
  employees: EmployeeOption[]
  can_view_others: boolean
  viewing_self: boolean
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function deriveActivityStatus(row: Activity): WorkflowStatus {
  if (row.paid_at || row.paid_na) return "paid"
  if (row.approved_at) return "approved"
  if (row.submitted_at) return "submitted"
  if (row.was_rejected) return "rejected"
  return "unsubmitted"
}

/* ── Constants ──────────────────────────────────────────── */

const PER_PAGE = 50

const TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "T", label: "Time" },
  { value: "E", label: "Expenses" },
  { value: "M", label: "Mileage" },
]

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Team", href: "/team" },
  { title: "Activity", href: "/team/activity" },
]

/* ── Payment Icons ──────────────────────────────────────── */

function PaidByIcon({ paidBy }: { paidBy: string | null }) {
  if (!paidBy) return null
  switch (paidBy) {
    case "Cash":
      return (
        <span className="text-green-600" title="Cash / Reimburse">
          $
        </span>
      )
    case "HPI AmEx":
      return (
        <span className="text-xs font-bold text-blue-600" title="HPI AmEx">
          AmEx
        </span>
      )
    case "Vendor":
      return (
        <span className="text-xs text-gray-500" title="Vendor to Invoice">
          Inv
        </span>
      )
    default:
      return null
  }
}

function ReceiptIcon({ receiptType }: { receiptType: string | null }) {
  if (!receiptType) return null
  const lower = receiptType.toLowerCase()
  if (lower.includes("pdf")) {
    return (
      <span className="text-xs text-red-500" title="PDF receipt">
        PDF
      </span>
    )
  }
  if (
    lower.includes("image") ||
    lower.includes("jpg") ||
    lower.includes("jpeg") ||
    lower.includes("png")
  ) {
    return (
      <span className="text-xs text-blue-500" title="Image receipt">
        IMG
      </span>
    )
  }
  return (
    <span className="text-xs text-gray-400" title={receiptType}>
      File
    </span>
  )
}

/* ── Columns ───────────────────────────────────────────── */

const columns: HpiColumn<Activity>[] = [
  {
    header: "Date",
    width: "w-[90px] !flex-none",
    render: (row) => (
      <div className="text-sm">{formatDate(row.activity_date)}</div>
    ),
  },
  {
    header: "Case",
    render: (row) =>
      row.case_name ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="truncate text-sm">{row.case_name}</div>
          </TooltipTrigger>
          <TooltipContent>{row.case_name}</TooltipContent>
        </Tooltip>
      ) : (
        <div className="text-sm">-</div>
      ),
  },
  {
    header: "Type",
    width: "w-[100px] !flex-none",
    render: (row) => (
      <div className="text-sm">
        <div className="flex items-center gap-1">
          {row.activity_type === "Expense" && (
            <PaidByIcon paidBy={row.paid_by} />
          )}
          <span>{row.activity_type}</span>
        </div>
        {row.sub_type && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-hpi-med-gray truncate text-xs">
                {row.sub_type}
              </div>
            </TooltipTrigger>
            <TooltipContent>{row.sub_type}</TooltipContent>
          </Tooltip>
        )}
      </div>
    ),
  },
  {
    header: "Description",
    render: (row) =>
      row.description ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="truncate text-sm">
              {row.activity_type === "Expense" && row.receipt_type && (
                <ReceiptIcon receiptType={row.receipt_type} />
              )}{" "}
              {row.description}
            </div>
          </TooltipTrigger>
          <TooltipContent>{row.description}</TooltipContent>
        </Tooltip>
      ) : (
        <div className="text-sm">-</div>
      ),
  },
  {
    header: "Status",
    width: "w-[100px] !flex-none",
    render: (row) => <WorkflowStatusBadge status={deriveActivityStatus(row)} />,
  },
  {
    header: "Added",
    width: "w-[88px] !flex-none",
    hideOnMobile: true,
    render: (row) => <div className="text-sm">{formatDate(row.added_at)}</div>,
  },
  {
    header: "Submitted",
    width: "w-[88px] !flex-none",
    hideOnMobile: true,
    render: (row) => (
      <div className="text-sm">{formatDate(row.submitted_at)}</div>
    ),
  },
  {
    header: "Paid",
    width: "w-[88px] !flex-none",
    hideOnMobile: true,
    render: (row) => (
      <div className="text-sm">
        {row.paid_na ? (
          <span className="text-hpi-med-gray">N/A</span>
        ) : (
          (row.paid_at ?? "")
        )}
      </div>
    ),
  },
]

/* ── Paginated Group Table ────────────────────────────────── */

function PaginatedActivityTable({
  title,
  activities,
  columns: cols,
}: {
  title: string
  activities: Activity[]
  columns: HpiColumn<Activity>[]
}) {
  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(activities.length / PER_PAGE)
  const paginated = activities.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  return (
    <HpiDataTable<Activity>
      title={title}
      columns={cols}
      data={paginated}
      keyFn={(row) => row.id}
      rowHref={(row) => {
        if (row.marketing_activity_id) {
          if (row.activity_type === "Time") {
            return `/marketing_activities/${row.marketing_activity_id}/time_entries/${row.source_id}`
          }
          return `/marketing_activities/${row.marketing_activity_id}/expenses/${row.source_id}`
        }
        if (!row.case_id) return undefined
        if (row.activity_type === "Time") {
          return `/cases/${row.case_id}/time_entries/${row.source_id}`
        }
        return `/cases/${row.case_id}/expenses/${row.source_id}`
      }}
      rowTarget="_blank"
      className="mb-6"
      pagination={
        activities.length > PER_PAGE ? (
          <HpiPagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={activities.length}
            perPage={PER_PAGE}
            onPageChange={setPage}
          />
        ) : undefined
      }
    />
  )
}

/* ── Summary Stats ─────────────────────────────────────── */

function buildStats(summary: Summary) {
  switch (summary.type) {
    case "time":
      return [
        { label: "OTJ", subLabel: "Time", value: summary.otj ?? "0:00" },
        { label: "Travel", subLabel: "Time", value: summary.travel ?? "0:00" },
        {
          label: "Mileage",
          subLabel: "Miles",
          value: `${summary.mileage ?? 0} mi`,
        },
      ]
    case "mileage":
      return [
        {
          label: "Total",
          subLabel: "Mileage",
          value: `${summary.mileage ?? 0} mi`,
        },
      ]
    case "expenses":
      return [
        {
          label: "Total",
          subLabel: "Expenses",
          value: formatCurrency(summary.total ?? 0),
        },
      ]
    default:
      return []
  }
}

/* ── Status Group Config ──────────────────────────────── */

const STATUS_GROUPS: {
  key: keyof GroupedActivities
  title: string
}[] = [
  { key: "unsubmitted", title: "Needs Submission" },
  { key: "rejected", title: "Rejected" },
  { key: "submitted", title: "Awaiting Approval" },
  { key: "approved", title: "Awaiting Payment" },
  { key: "paid", title: "Completed" },
]

/* ── Main Component ────────────────────────────────────── */

export default function TeamActivity({
  employee,
  activities,
  grouped_activities,
  start_date,
  end_date,
  type_filter,
  summary,
  employees,
  can_view_others,
  viewing_self,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [typeFilter, setTypeFilter] = useState(type_filter)
  const [selectedEmployee, setSelectedEmployee] = useState(String(employee.id))

  const viewingAll = employee.id === 0

  const activeColumns = useMemo(() => {
    if (!viewingAll) return columns
    return [
      columns[0], // Date
      {
        header: "Employee",
        width: "w-[120px] !flex-none",
        render: (row: Activity) => (
          <div className="truncate text-sm">{row.employee_name || ""}</div>
        ),
      },
      ...columns.slice(1),
    ]
  }, [viewingAll])

  const employeeOptions = useMemo(
    () => [
      { value: "0", label: "All" },
      ...employees.map((emp) => ({ value: String(emp.id), label: emp.name })),
    ],
    [employees],
  )

  const handleUpdate = () => {
    const params: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
    }
    if (typeFilter) params.type = typeFilter
    if (can_view_others && selectedEmployee)
      params.employee_id = selectedEmployee
    router.get("/team/activity", params, { preserveState: true })
  }

  const stats = buildStats(summary)

  // "Your Entries" pinned section: show when admin/manager is viewing all
  // employees (not a specific one) and has actionable entries of their own
  const myActionable =
    can_view_others && !viewing_self
      ? activities.filter(
          (a) =>
            a.is_mine &&
            (deriveActivityStatus(a) === "unsubmitted" ||
              deriveActivityStatus(a) === "rejected"),
        )
      : []

  const hasAnyGroups = STATUS_GROUPS.some(
    (g) => grouped_activities[g.key].length > 0,
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Activity - ${employee.name}`} />

      <div className="p-6">
        {/* Filter Bar */}
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        >
          <HpiFilterSelect
            label="Type"
            value={typeFilter}
            onChange={setTypeFilter}
            options={TYPE_OPTIONS}
            placeholder="All"
          />
          {can_view_others && employees.length > 0 && (
            <HpiFilterSelect
              label="Employee"
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              options={employeeOptions}
              placeholder="Select..."
            />
          )}
        </HpiDateFilterBar>

        {/* Your Entries — pinned section for admins with actionable items */}
        {myActionable.length > 0 && (
          <PaginatedActivityTable
            title={`Your Entries (${myActionable.length})`}
            activities={myActionable}
            columns={activeColumns}
          />
        )}

        {/* Grouped Tables */}
        {hasAnyGroups ? (
          STATUS_GROUPS.map(
            (group) =>
              grouped_activities[group.key].length > 0 && (
                <PaginatedActivityTable
                  key={group.key}
                  title={`${group.title} (${grouped_activities[group.key].length})`}
                  activities={grouped_activities[group.key]}
                  columns={activeColumns}
                />
              ),
          )
        ) : (
          <HpiDataTable<Activity>
            title={`Activity - ${employee.name}`}
            columns={activeColumns}
            data={[]}
            keyFn={(row) => row.id}
          />
        )}

        {/* Summary Stats */}
        {stats.length > 0 && <HpiStatsBar className="mt-6" stats={stats} />}
      </div>
    </AppLayout>
  )
}
